import { type ExportApiId, type ExportV1 } from '@/components/Studies/StudyExports/ExportsList/Exports.model';
import { buildColumns, generateFilterOptions } from '@/components/Studies/StudyExports/ExportsList/ExportsList.utils';
import APITable from '@/components/UI/Table/Reusable/APITable';
import type { UseTableProps } from '@/components/UI/Table/Reusable/APITable/APITable.model';
import { errorToast, successToast } from '@/helpers';
import { _isEmpty, _notNil } from '@/littledash';
import type { State } from '@/model/State.model';
import type { Study } from '@/model/Study.model';
import type { BaseUser } from '@/model/User.model';
import * as Auth from '@/support/auth.ts';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { RouteService } from '@/support/RouteService';
import { useModalAction } from '@/utils/modal';
import { useCallback, useMemo, type FC } from 'react';
import { useSelector } from 'react-redux';

interface ExportsListProps {
  study: Study;
}

const ExportsList: FC<ExportsListProps> = ({ study }) => {
  const studyApiId = study.api_id;
  const { openModal } = useModalAction();
  const isTeamOwner = Auth.isCurrentTeamOwner();
  const users = useSelector((state: State) => (state.team?.team?.users ?? []) as Array<BaseUser>);
  const filterOptions = useMemo(() => generateFilterOptions(users), [users]);

  const { invoke: invokeDownloadUrl } = useApiHook({
    endpoint: 'GET /api/v1/studies/{studyApiId}/exports/{exportApiId}/download-url',
    options: { invokeOnInit: false, onError: { toast: false, throw: true, slug: 'export-download-url' } },
  });

  const { invoke: invokeBulkDelete } = useApiHook({
    endpoint: 'DELETE /api/v1/studies/{studyApiId}/exports',
    options: { invokeOnInit: false, onError: { toast: false, throw: true, slug: 'export-bulk-delete' } },
  });

  const { invoke: invokeSetVisibility } = useApiHook({
    endpoint: 'PATCH /api/v1/studies/{studyApiId}/exports/{exportApiId}/visibility',
    options: { invokeOnInit: false, onError: { toast: false, throw: true, slug: 'export-set-visibility' } },
  });

  const currentUserApiId = useSelector((state: State) => state.user.currentUser?.api_id);

  const handleDownload = useCallback(
    async (exportApiId: ExportApiId, fileName: string | null) => {
      try {
        const result = await invokeDownloadUrl({ path: { studyApiId, exportApiId } });
        const url = result.body?.url;
        if (!_notNil(url)) {
          errorToast('Could not get download link.');
          return;
        }
        const response = await fetch(url);
        if (!response.ok) {
          errorToast('Could not download export.');
          return;
        }
        const blob = await response.blob();
        const objectUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.setAttribute('download', fileName ?? 'export');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(objectUrl);
      } catch {
        errorToast('Could not download export.');
      }
    },
    [studyApiId, invokeDownloadUrl]
  );

  const handleRename = useCallback(
    (row: ExportV1, onRenamed: () => Promise<void>) => {
      openModal('RENAME_EXPORT', {
        studyApiId,
        exportApiId: row.api_id,
        currentFileName: row.file_name,
        onRenamed,
      });
    },
    [openModal, studyApiId]
  );

  const handleSetVisibility = useCallback(
    async (row: ExportV1, isPublic: boolean, refetch: () => Promise<void>) => {
      try {
        await invokeSetVisibility({
          path: { studyApiId, exportApiId: row.api_id },
          body: { is_public: isPublic },
        });
      } catch {
        errorToast('Could not update export visibility');
        return;
      }
      successToast(isPublic ? 'Export is now public' : 'Export is now private');
      await refetch();
    },
    [studyApiId, invokeSetVisibility]
  );

  const handleBulkDelete = useCallback(
    async (apiIds: Array<ExportApiId>, reasonForChange: string | undefined, refetchTableData: () => Promise<void>) => {
      if (_isEmpty(apiIds)) {
        return;
      }
      try {
        await invokeBulkDelete({
          path: { studyApiId },
          body: { api_ids: apiIds, reason_for_change: reasonForChange },
        });
      } catch {
        errorToast(apiIds.length === 1 ? 'Could not delete export' : 'Could not delete exports');
        return;
      }
      successToast(apiIds.length === 1 ? 'Export deleted' : `${apiIds.length} exports deleted`);
      await refetchTableData();
    },
    [studyApiId, invokeBulkDelete]
  );

  const columns = useMemo(
    () =>
      buildColumns({
        isTeamOwner,
        currentUserApiId,
        onDownload: handleDownload,
        onRename: handleRename,
        onSetVisibility: handleSetVisibility,
      }),
    [handleDownload, handleRename, handleSetVisibility, isTeamOwner, currentUserApiId]
  );

  const bulkActions = useCallback(
    ({
      useTableProps: {
        selectedFlatRows,
        apiTable: { fetchTableData },
      },
    }: UseTableProps<ExportV1>) => [
      {
        name: 'Delete',
        key: 'delete_exports',
        className: 'red',
        disabled: _isEmpty(selectedFlatRows),
        action: () => {
          const apiIds = selectedFlatRows.reduce<Array<ExportApiId>>((acc, { original }) => {
            if (_notNil(original.api_id)) {
              acc.push(original.api_id);
            }
            return acc;
          }, []);
          openModal('CONFIRM_DELETE_EXPORTS', {
            count: apiIds.length,
            onClick: (reason?: string) => handleBulkDelete(apiIds, reason, fetchTableData),
          });
        },
      },
    ],
    [handleBulkDelete, openModal]
  );

  return (
    <div className="mv3" data-testid="exports-list">
      <APITable
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/studies/{studyApiId}/exports',
            path: { studyApiId },
          }).url,
        }}
        columns={columns}
        defaultSortBy={{ id: 'created_at', desc: true }}
        reduxTableName="studyExports"
        filterOptions={filterOptions}
        searchPlaceholderText="Search by File name"
        bulkActions={bulkActions}
      />
    </div>
  );
};

export default ExportsList;
